import { sendPrintEventToAgent } from '@/services/printing/printAgentService';
import {
  escapeText,
  formatDateTime,
  normalizeNumber,
  printTicketHtml,
} from '@/services/printing/ticketReceiptPrinter';
import { renderOperationalPrintEvent } from '@/services/printing/operationalPrintTemplateService';
import { buildPrintJob, toPrintAgentPayload } from '@/services/printing/printJobService';
import { isAgentConnectionError } from '@/services/printing/printAgentError';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function extractItems(payload = {}) {
  if (Array.isArray(payload.items) && payload.items.length) {
    return payload.items;
  }

  if (Array.isArray(payload.shopping_carts) && payload.shopping_carts.length) {
    return payload.shopping_carts;
  }

  return [];
}

function getSelectedStepPlates(step = {}) {
  if (Array.isArray(step.plates_selected)) return step.plates_selected;
  if (Array.isArray(step.plates)) return step.plates;
  if (step.plate_selected) return [step.plate_selected];
  return [];
}

function buildStepDetails(item = {}) {
  const steps = Array.isArray(item.steps_selected)
    ? item.steps_selected
    : Array.isArray(item.steps)
      ? item.steps
      : [];

  return steps
    .map((step, index) => {
      const selectedNames = getSelectedStepPlates(step)
        .map((selected) => selected?.plate?.name || selected?.custom_name || selected?.name || selected?.plate_name || '')
        .filter(Boolean);

      if (!selectedNames.length) return null;

      return `${step?.name || step?.title || `Paso ${index + 1}`}: ${selectedNames.join(', ')}`;
    })
    .filter(Boolean);
}

function buildKitchenPayload(payload = {}) {
  const items = extractItems(payload).map((item) => ({
    quantity: item.amount ?? item.quantity ?? 0,
    name: item.plate_name || item.product_name_snapshot || item.name || item.plate?.name || 'Producto',
    note: escapeText(item.comment || item.notes || ''),
    details: buildStepDetails(item),
    category_id: String(
      item.category_id
      || item.product_category_id
      || item.category?.id
      || item.plate?.category_id
      || item.plate?.category?.id
      || ''
    ) || null,
  }));

  const restaurantName = payload.restaurantName || payload.restaurant_name || 'Eat with Us TPV';
  const title = payload.title || 'COMANDA';
  const tableLabel = payload.tableLabel || payload.table_label || '-';
  const orderLabel = payload.orderLabel
    || payload.order_label
    || (payload.order_id ? `Pedido #${payload.order_id}` : 'Pedido TPV');
  const createdAt = payload.createdAt || payload.created_at || Date.now();
  const footerText = payload.footerText || 'Para cocina';

  return {
    restaurantName,
    title,
    tableLabel,
    orderLabel,
    createdAt,
    footerText,
    items,
  };
}

export function buildKitchenTicketText(payload = {}) {
  const normalized = buildKitchenPayload(payload);
  const lines = [
    '--------------------------------',
    ` ${normalized.restaurantName}`,
    ` ${normalized.title}`,
    '--------------------------------',
    `Mesa: ${normalized.tableLabel}`,
    `Fecha: ${formatDateTime(normalized.createdAt)}`,
    '--------------------------------',
  ];

  normalized.items.forEach((item) => {
    const quantity = Math.max(1, Math.round(normalizeNumber(item.quantity)));
    lines.push(`${quantity}x ${item.name}`);
    if (item.note) {
      lines.push(`  Nota: ${item.note}`);
    }
    (item.details || []).forEach((line) => {
      lines.push(`  ${line}`);
    });
    lines.push('');
  });

  lines.push('--------------------------------');
  lines.push(normalized.footerText);
  lines.push('');

  return lines.join('\n');
}

export function buildKitchenTicketHtml(payload = {}) {
  const normalized = buildKitchenPayload(payload);
  const itemsRows = normalized.items.map((item) => {
    const details = Array.isArray(item.details) ? item.details : [];
    const noteLines = item.note ? [`Observacion: ${item.note}`] : [];
    const detailLines = [...noteLines, ...details]
      .map((detail) => `<div class="item-step-option">&bull; ${escapeHtml(detail)}</div>`)
      .join('');

    return `
      <tbody data-print-category-id="${escapeHtml(item.category_id || '')}">
      <tr>
        <td class="col-qty">${Math.max(1, Math.round(normalizeNumber(item.quantity)))}x</td>
        <td class="col-product"><span class="item-name">${escapeHtml(item.name)}</span></td>
      </tr>
      ${detailLines ? `
      <tr class="item-modifiers-row">
        <td colspan="2">
          <table class="item-modifiers-table">
            <tr>
              <td>${detailLines}</td>
            </tr>
          </table>
        </td>
      </tr>` : ''}
      </tbody>
    `;
  }).join('');

  return `
    <style>
      @page {
        size: 80mm auto;
        margin: 8mm 9mm 7mm;
      }
      html,
      body {
        margin: 0;
        padding: 0;
        background: #fff;
      }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 10px;
        color: #000;
        line-height: 1.18;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .ticket-shell {
        width: 100%;
        margin: 0;
        padding: 0;
      }
      .header-section {
        text-align: center;
      }
      .restaurant-name {
        font-size: 17px;
        font-weight: 900;
        line-height: 1;
      }
      .restaurant-info {
        padding-top: 0.8mm;
        font-size: 8px;
        font-weight: 700;
        line-height: 1;
        text-transform: uppercase;
      }
      .header-spacer {
        height: 7mm;
      }
      .items-spacer {
        height: 7mm;
      }
      .footer-spacer {
        height: 12mm;
      }
      .meta-table,
      .items-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }
      .meta-table td {
        padding: 0.65mm 0;
        font-size: 10px;
        vertical-align: top;
      }
      .meta-label {
        width: 42%;
        font-weight: 900;
        text-transform: uppercase;
        white-space: nowrap;
      }
      .meta-value {
        width: 58%;
        text-align: right;
        font-weight: 900;
        white-space: nowrap;
      }
      .items-table thead td {
        border-bottom: 3px solid #000;
        padding: 0 0 1.1mm;
        font-size: 10px;
        font-weight: 900;
        text-transform: uppercase;
      }
      .items-table tbody td {
        padding: 1.35mm 0 0;
        font-size: 10px;
        vertical-align: top;
      }
      .col-qty { width: 11%; font-weight: 900; }
      .col-product { width: 89%; }
      .item-name { font-weight: 900; }
      .item-modifiers-row td {
        padding-top: 0.6mm;
      }
      .item-modifiers-table {
        width: 89%;
        margin-left: 11%;
        border-collapse: collapse;
      }
      .item-modifiers-table td {
        border-left: 2px solid #000;
        padding: 0 0 0.45mm 3mm;
      }
      .item-step-option {
        font-size: 9px;
        padding-bottom: 0.25mm;
      }
      .footer-section {
        text-align: center;
      }
      .thanks-msg {
        font-size: 12px;
        font-weight: 900;
        text-transform: uppercase;
      }
      .web-link {
        padding-top: 2mm;
        font-size: 9px;
        font-weight: 700;
      }
    </style>
    <div class="ticket-shell">
      <div class="header-section">
        <div class="restaurant-name">${escapeHtml(normalized.restaurantName)}</div>
        <div class="restaurant-info">${escapeHtml(normalized.title)}</div>
      </div>
      <div class="header-spacer"></div>
      <table class="meta-table">
        <tr>
          <td class="meta-label">MESA:</td>
          <td class="meta-value">${escapeHtml(normalized.tableLabel)}</td>
        </tr>
        <tr>
          <td class="meta-label">FECHA:</td>
          <td class="meta-value">${escapeHtml(formatDateTime(normalized.createdAt))}</td>
        </tr>
      </table>
      <div class="items-spacer"></div>
      <table class="items-table">
        <thead>
          <tr>
            <td class="col-qty"></td>
            <td class="col-product">Producto</td>
          </tr>
        </thead>
        ${itemsRows}
      </table>
      <div class="footer-spacer"></div>
      <div class="footer-section">
        <div class="thanks-msg">COMANDA</div>
        <div class="web-link">${escapeHtml(normalized.footerText)}</div>
      </div>
    </div>
  `;
}

export async function printKitchenTicket(payload = {}) {
  if (typeof window === 'undefined') return false;

  const eventKey = payload.print_event || payload.metadata?.print_event || 'manual_kitchen_print';
  const browserFallbackOnAgentUnavailable = payload.browserFallbackOnAgentUnavailable === true;
  let html = buildKitchenTicketHtml(payload);
  let renderedTemplate = null;
  let templateSource = 'legacy';
  let templateMetadata = {};

  try {
    const rendered = await renderOperationalPrintEvent(eventKey, payload, {
      defaultTitle: 'COMANDA',
      footerMessage: payload.footerText || 'Para cocina',
      originLabel: eventKey === 'pos_order_created' ? 'TPV' : 'Manual',
      showPrices: false,
    });
    html = rendered.documentHtml;
    renderedTemplate = rendered.template;
    templateSource = rendered.source;
    templateMetadata = {
      print_template_key: rendered.template?.template_key || 'order_command',
      print_template_source: rendered.source || 'unknown',
      print_template_version: rendered.template?.version || null,
      print_template_payload: rendered.payload,
    };
  } catch (error) {
    console.warn('No se pudo renderizar la plantilla de cocina, se usa el HTML legado:', error);
  }

  try {
    const printJob = buildPrintJob({
      eventKey,
      htmlContent: html,
      template: renderedTemplate,
      templateSource,
      metadata: {
        ...(payload.metadata || {}),
        ...templateMetadata,
      },
    });
    const response = await sendPrintEventToAgent(toPrintAgentPayload(printJob, {
      printer_name: payload.printer_name || null,
    }), eventKey);

    if (response?.printed || response?.accepted) {
      return true;
    }
    if (response?.transport === 'restaurant') return false;

    if (response?.usedConfiguredFlow) {
      if (browserFallbackOnAgentUnavailable && isAgentConnectionError(response?.error)) {
        return printTicketHtml(html);
      }
      console.warn('El flujo configurado de impresión de comanda no se pudo completar:', response?.error || 'sin detalle');
      return false;
    }

    console.warn('No se pudo imprimir la comanda con el agente local:', response?.error || 'sin detalle');
    if (browserFallbackOnAgentUnavailable && isAgentConnectionError(response?.error)) {
      return printTicketHtml(html);
    }
  } catch (error) {
    console.warn('No se pudo imprimir la comanda con el agente local:', error);
    if (browserFallbackOnAgentUnavailable && isAgentConnectionError(error?.message || error)) {
      return printTicketHtml(html);
    }
  }

  return false;
}
